/**
 * @file AddMessageTabsPage.js
 * @description Component for adding a new message with multiple tabs.
 **/
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styles from './AddMessageTabsPage.module.scss';
import Toast from '../ui/toasts/Toast';
import DetailsTab from './DetailsTab';
import ContentTab from './ContentTab';
import {
    getMessage,
    addMessage,
    updateMessage,
} from '../../services/clientService';
import { useAuthContext } from '../../store/Contexts';
import { useAdminAudienceEnabled } from '../../hooks/useAdminAudienceEnabled';
import { ROUTES, ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';
import { htmlToPlainText } from '../../utils/htmlText';
import { PushDestinationType } from '../../utils/data/enums';
import NotificationPageFrame from './NotificationPageFrame';

const PUSH_DESTINATION_TYPE_BY_FIELD = {
    'full-message': PushDestinationType.FullMessage,
    portal: PushDestinationType.Portal,
    menu: PushDestinationType.Menu,
    sso: PushDestinationType.SSO,
    website: PushDestinationType.WebsiteUrl,
    asset: PushDestinationType.Asset,
};

const PUSH_DESTINATION_FIELD_BY_TYPE = {
    [PushDestinationType.FullMessage]: 'full-message',
    [PushDestinationType.Portal]: 'portal',
    [PushDestinationType.Menu]: 'menu',
    [PushDestinationType.SSO]: 'sso',
    [PushDestinationType.WebsiteUrl]: 'website',
    [PushDestinationType.Asset]: 'asset',
};

const PUSH_DESTINATION_VALUE_IS_NUMERIC = {
    [PushDestinationType.Portal]: true,
    [PushDestinationType.SSO]: true,
    [PushDestinationType.Asset]: true,
};

const NOTIFICATION_FREQUENCY_AD_HOC = 2;

function buildPushDestinationFields(pushDestination, pushLink) {
    const pushDestinationTypeId =
        PUSH_DESTINATION_TYPE_BY_FIELD[pushDestination] ??
        PushDestinationType.FullMessage;
    const hasDestinationValue = pushLink !== undefined && pushLink !== '';

    return {
        pushDestinationTypeId,
        pushDestinationValue: hasDestinationValue ? String(pushLink) : null,
    };
}

function parsePushDestinationFields({
    pushDestinationTypeId,
    pushDestinationValue,
}) {
    if (!(pushDestinationTypeId in PUSH_DESTINATION_FIELD_BY_TYPE)) {
        return { pushDestination: 'full-message', pushLink: '' };
    }

    return {
        pushDestination: PUSH_DESTINATION_FIELD_BY_TYPE[pushDestinationTypeId],
        pushLink: PUSH_DESTINATION_VALUE_IS_NUMERIC[pushDestinationTypeId]
            ? Number(pushDestinationValue)
            : pushDestinationValue,
    };
}

/**
 * Component for adding a new message with multiple tabs.
 * @returns {JSX.Element} AddMessageTabsPage component.
 */
const AddMessageTabsPage = ({ isCreating = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { messageId } = useParams();
    const { user } = useAuthContext();
    const { isAdminAudienceEnabled } = useAdminAudienceEnabled();
    const [messageData, setMessageData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [contentSeedKey, setContentSeedKey] = useState(null);
    const [isNextMenuOpen, setIsNextMenuOpen] = useState(false);
    const isTemplateSeededRef = useRef(false);
    const nextMenuRef = useRef(null);

    const loadMessage = async () => {
        setIsLoading(true);
        try {
            const data = await getMessage(null, messageId);

            const hydratedMessage = {
                ...data,
                ...parsePushDestinationFields(data),
            };

            setMessageData(hydratedMessage);
            setContentSeedKey(
                `notification-${data.id ?? 'loading'}-${data.updatedAt ?? data.createdAt ?? ''}`,
            );
        } catch (error) {
            console.error('Error retrieving message record:', error);
            Toast.error(getText('msg_839027c7bdcf'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isCreating && messageId) {
            loadMessage();
        }
    }, [messageId, isCreating]);

    useEffect(() => {
        const template = location.state?.notificationTemplate;
        if (!isCreating || !template) return;

        isTemplateSeededRef.current = true;

        const hydratedTemplate = {
            ...template,
            ...parsePushDestinationFields(template),
            id: undefined,
            notificationId: undefined,
            timezone: undefined,
            notificationStatusId: 1,
        };

        setMessageData(hydratedTemplate);
        setContentSeedKey(
            `template-${template.id ?? template.name ?? 'blank'}-${template.updatedAt ?? template.subject ?? template.name ?? ''}`,
        );

        navigate(location.pathname, {
            replace: true,
            state: {
                scheduleAfterSave: Boolean(location.state?.scheduleAfterSave),
            },
        });
    }, [isCreating, location.state, navigate, location.pathname]);

    const MAX_EMAIL_SIZE_BYTES = 20 * 1024 * 1024;
    const MAX_SMS_LENGTH = 15000;
    const MAX_PUSH_LENGTH = 178;

    const getHtmlByteSize = html => new Blob([html || '']).size;

    const hasMeaningfulHtml = html => {
        if (!html) return false;
        return (
            html
                .replace(/<[^>]*>/g, '')
                .replace(/&nbsp;/g, ' ')
                .trim().length > 0
        );
    };

    const isEmailFormatted = hasMeaningfulHtml(messageData.htmlBody);
    const isSmsFormatted = hasMeaningfulHtml(messageData.plainTextBody);
    const isPushFormatted = hasMeaningfulHtml(messageData.pushBody);

    const validateDetailsSection = () => {
        if (!messageData.name?.trim()) {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }
        if (!messageData.notificationCategoryId) {
            Toast.error(getText('msg_ba3dceb428c5'));
            return false;
        }
        return true;
    };

    const validateContentSection = () => {
        if (!messageData.subject?.trim()) {
            Toast.error(getText('msg_bd3159ff2120'));
            return false;
        }
        if (getHtmlByteSize(messageData.htmlBody) > MAX_EMAIL_SIZE_BYTES) {
            Toast.error(
                getText('msg_8985fe967539'),
                null,
                getText('msg_ba07b4a57f8f'),
            );
            return false;
        }
        if (
            htmlToPlainText(messageData.plainTextBody).length > MAX_SMS_LENGTH
        ) {
            Toast.error(
                getText('msg_b148ea20f3f2'),
                null,
                getText('msg_fff64a1bdbea'),
            );
            return false;
        }
        if (htmlToPlainText(messageData.pushBody).length > MAX_PUSH_LENGTH) {
            Toast.error(
                getText('msg_b148ea20f3f2'),
                null,
                getText('msg_f0053e8be69d'),
            );
            return false;
        }
        if (!isEmailFormatted || !isSmsFormatted || !isPushFormatted) {
            Toast.error(
                getText('msg_cc0a768e722b'),
                null,
                getText('msg_7887042f5d83'),
            );
            return false;
        }
        return true;
    };

    const validateForm = () =>
        validateDetailsSection() && validateContentSection();

    const handleMessageDataChange = useCallback(patch => {
        setMessageData(prev => {
            const nextPatch = Object.fromEntries(
                Object.entries(patch || {}).filter(
                    ([, value]) => value !== undefined,
                ),
            );

            return {
                ...prev,
                ...nextPatch,
            };
        });
    }, []);

    const preprocessHtml = html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const images = doc.querySelectorAll('img');

        images.forEach(img => {
            const style = img.getAttribute('style') || '';
            const widthMatch = style.match(/width\s*:\s*([^;]+);?/i);
            const heightMatch = style.match(/height\s*:\s*([^;]+);?/i);

            if (widthMatch) {
                const widthValue = widthMatch[1].trim().replace(/px$/i, '');
                if (img.getAttribute('width') !== widthValue) {
                    img.setAttribute('width', widthValue);
                }
            }

            if (heightMatch) {
                const heightValue = heightMatch[1].trim().replace(/px$/i, '');
                if (img.getAttribute('height') !== heightValue) {
                    img.setAttribute('height', heightValue);
                }
            }
        });

        let preprocessedHtml = doc.body.innerHTML;

        return preprocessedHtml;
    };

    const handleSave = async ({ scheduleAfterSave = false } = {}) => {
        if (isLoading) return;

        if (!validateForm()) {
            return;
        }

        const messagePayload = {
            ...messageData,
            htmlBody: preprocessHtml(messageData.htmlBody),
            plainTextBody: htmlToPlainText(messageData.plainTextBody),
            pushBody: htmlToPlainText(messageData.pushBody),
            ...buildPushDestinationFields(
                messageData.pushDestination,
                messageData.pushLink,
            ),
        };

        if (
            isAdminAudienceEnabled &&
            isCreating &&
            messagePayload.dataFilterId == null
        ) {
            messagePayload.dataFilterId = user?.jwt?.df;
        }

        delete messagePayload.dataFilter;
        delete messagePayload.pushDestination;
        delete messagePayload.pushLink;
        delete messagePayload.timezone;

        setIsLoading(true);
        try {
            if (isCreating) {
                Object.assign(messagePayload, { notificationStatusId: 1 });

                const createdNotification = await addMessage(
                    null,
                    messagePayload,
                );

                Toast.success(
                    getText('msg_4750271dfc6e', {
                        name: messagePayload.name,
                    }),
                );

                const createdNotificationId =
                    createdNotification?.id ??
                    createdNotification?.notificationId ??
                    createdNotification?.data?.id;

                const shouldScheduleAfterSave =
                    scheduleAfterSave ||
                    Boolean(location.state?.scheduleAfterSave);

                if (shouldScheduleAfterSave && createdNotificationId) {
                    navigate(
                        ROUTE_BUILDERS.notificationScheduleNew(
                            createdNotificationId,
                        ),
                    );
                    return;
                }

                navigate(ROUTES.MESSAGES);
            } else {
                const updatedNotification = await updateMessage(
                    null,
                    messagePayload,
                );
                Toast.success(
                    getText('msg_d30654fe55b0', {
                        name: messagePayload.name,
                    }),
                );

                const shouldScheduleAfterSave =
                    scheduleAfterSave ||
                    Boolean(location.state?.scheduleAfterSave);
                const updatedNotificationId =
                    updatedNotification?.id ??
                    updatedNotification?.notificationId ??
                    messageId;

                if (shouldScheduleAfterSave && updatedNotificationId) {
                    navigate(
                        ROUTE_BUILDERS.notificationScheduleNew(
                            updatedNotificationId,
                        ),
                    );
                    return;
                }

                navigate(ROUTES.MESSAGES);
            }
        } catch (error) {
            Toast.error(getText('msg_7d0875417067'));
        } finally {
            setIsLoading(false);
        }
    };

    const contentSeeds = {
        subjectSeed: messageData?.subject || '',
        emailEditorSeed: messageData?.htmlBody || '',
        smsEditorSeed: messageData?.plainTextBody || '',
        pushEditorSeed: messageData?.pushBody || '',
    };

    const isCreatingFromTemplate =
        isCreating &&
        (Boolean(location.state?.notificationTemplate) ||
            isTemplateSeededRef.current);

    const pageTitle =
        isCreatingFromTemplate &&
        (messageData?.name || location.state?.notificationTemplate?.name)
            ? messageData.name || location.state?.notificationTemplate?.name
            : isCreating
              ? getText('msg_8fbc0ffca3f0')
              : getText('msg_2f4889e44dfb');

    const handleClose = useCallback(() => {
        if (isCreatingFromTemplate) {
            navigate(ROUTES.MESSAGES_TEMPLATES);
            return;
        }

        navigate(ROUTES.MESSAGES);
    }, [isCreatingFromTemplate, navigate]);

    useEffect(() => {
        const handleClickOutside = event => {
            if (
                nextMenuRef.current &&
                !nextMenuRef.current.contains(event.target)
            ) {
                setIsNextMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <NotificationPageFrame
            title={pageTitle}
            titleClassName={`page-title float-start ms-4 m-0 ${isCreatingFromTemplate ? 'col-6' : 'col-4'}`}
            headerClassName={`${isCreatingFromTemplate ? 'col-4' : 'col-auto'} ${styles.headerContent} ${isCreatingFromTemplate ? styles.templateModeHeaderContent : ''}`}
            headerContent={
                <>
                    {!isCreatingFromTemplate && (
                        <button
                            type="button"
                            onClick={handleSave}
                            className={styles.headerActionButton}
                            disabled={isLoading}
                        >
                            <i className="bi bi-check-lg me-2" />
                            {getText('msg_efc007a393f6')}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleClose}
                        className={styles.headerActionButton}
                    >
                        <i className="bi bi-x me-2" />
                        {getText('msg_bbfa773e5a63')}
                    </button>
                </>
            }
            bodyClassName={`${styles.editorBody} ${isCreatingFromTemplate ? styles.templateModeEditorBody : ''}`}
            footerContent={
                <div
                    className={`${styles.templateFlowFooter} ${isCreatingFromTemplate ? styles.templateModeFooter : ''}`}
                >
                    <button
                        type="button"
                        className={`button-base button-secondary ${styles.footerButton}`}
                        onClick={handleClose}
                    >
                        {getText('msg_77dfd2135f4d')}
                    </button>

                    <div className={styles.nextMenuWrapper} ref={nextMenuRef}>
                        <button
                            type="button"
                            className={`button-base ing-button-primary-left ${styles.footerButton}`}
                            onClick={() => setIsNextMenuOpen(prev => !prev)}
                            disabled={isLoading}
                        >
                            {getText('msg_bc981983e7f5')}
                        </button>

                        {isNextMenuOpen && (
                            <div className={styles.nextMenuPopover}>
                                <button
                                    type="button"
                                    className={styles.nextMenuItem}
                                    onClick={() => {
                                        setIsNextMenuOpen(false);
                                        handleSave({
                                            scheduleAfterSave: false,
                                        });
                                    }}
                                >
                                    {getText('msg_efc007a393f6')}
                                </button>

                                <button
                                    type="button"
                                    className={styles.nextMenuItem}
                                    onClick={() => {
                                        setIsNextMenuOpen(false);
                                        handleSave({
                                            scheduleAfterSave: true,
                                        });
                                    }}
                                >
                                    {getText('msg_0a8adac9d6d5')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            }
        >
            <DetailsTab
                data={messageData}
                onChange={handleMessageDataChange}
                isCreating={isCreating}
                user={user}
                useWideLayout={isCreatingFromTemplate}
            />

            <ContentTab
                data={messageData}
                contentSeedKey={contentSeedKey}
                subjectSeed={contentSeeds.subjectSeed}
                emailEditorSeed={contentSeeds.emailEditorSeed}
                smsEditorSeed={contentSeeds.smsEditorSeed}
                pushEditorSeed={contentSeeds.pushEditorSeed}
                onChange={handleMessageDataChange}
                useWideLayout={isCreatingFromTemplate}
            />
        </NotificationPageFrame>
    );
};

export default AddMessageTabsPage;
